import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getCurrentIdToken } from "../../firebase/firebaseClient";

// The Alert list's own route (MyFlightAlerts.tsx / App.js) -- Shared Login's allowlist
// (SHARED_LOGIN_ALLOWED_DESTINATIONS on the backend) must include this exact string too.
export const ALERT_LIST_DESTINATION = "/flight-alerts/list";

/**
 * "Flight lookup gains a sign-in link" (DASH-6345 UI, Flow step 1). Checks for a live Firebase
 * session on mount rather than assuming one: "already signed in -> Flight lookup links straight
 * to the Alert list" is a distinct behaviour from the visitor path, not just cosmetic label text
 * -- a signed-in tap skips Shared Login's phone/OTP round trip entirely.
 */
export const FlightAlertsSignInLink: React.FC = () => {
  const { t } = useTranslation("flightAlerts");
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentIdToken()
      .then((token) => {
        if (!cancelled) setIsSignedIn(Boolean(token));
      })
      .catch(() => {
        if (!cancelled) setIsSignedIn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auth state not resolved yet -- render nothing rather than guess, same reasoning as
  // useMyFlightAlerts starting in a loading state instead of assuming signed-out.
  if (isSignedIn === null) return null;

  const handleClick = () => {
    if (isSignedIn) {
      navigate(ALERT_LIST_DESTINATION);
      return;
    }
    navigate(`/shared-login?destination=${encodeURIComponent(ALERT_LIST_DESTINATION)}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-fit font-lexend text-[14px]/[18px] font-bold tracking-normal text-brand-blue"
    >
      {isSignedIn ? t("shared.viewMyAlertsLink") : t("shared.signInLink")}
    </button>
  );
};
